import { useRef, useState, DragEvent } from 'react';
import { Bold, Italic, Link2, List, Code, Image as ImageIcon, Loader2, X, RotateCw } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { uploadForumImageWithProgress, UploadError } from '@/lib/forumUploads';
import { Markdown } from './Markdown';

type Uploader = (file: File, onProgress?: (pct: number) => void) => Promise<string>;

interface MarkdownEditorProps {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  maxLength?: number;
  placeholder?: string;
  minHeight?: number;
  /** Custom upload function. Defaults to forum image uploader. */
  uploader?: Uploader;
}

type Pending = {
  id: string;
  file: File;
  name: string;
  previewUrl: string;
  progress: number;
  error?: string;
};

export function MarkdownEditor({
  value,
  onChange,
  rows = 6,
  maxLength = 4000,
  placeholder,
  minHeight,
  uploader,
}: MarkdownEditorProps) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const { toast } = useToast();
  const [pending, setPending] = useState<Pending[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  const insertAtCursor = (text: string) => {
    const ta = ref.current;
    if (!ta) {
      onChange(valueRef.current + text);
      return;
    }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const next = valueRef.current.slice(0, start) + text + valueRef.current.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + text.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  const wrapAtCursor = (before: string, after: string, placeholderText: string) => {
    const ta = ref.current;
    if (!ta) {
      onChange(valueRef.current + before + placeholderText + after);
      return;
    }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = valueRef.current.slice(start, end) || placeholderText;
    const next = valueRef.current.slice(0, start) + before + selected + after + valueRef.current.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + before.length + selected.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  const replacePlaceholder = (token: string, replacement: string) => {
    const current = valueRef.current;
    const next = current.includes(token) ? current.replace(token, replacement) : current + replacement;
    onChange(next);
  };

  const beginUpload = (file: File) => {
    const id = crypto.randomUUID();
    let previewUrl = '';
    try { previewUrl = URL.createObjectURL(file); } catch { /* noop */ }
    const placeholder = `![Uploading ${file.name}…](uploading:${id})`;
    insertAtCursor(`${placeholder}\n`);

    setPending((prev) => [...prev, { id, file, name: file.name, previewUrl, progress: 0 }]);

    const upload = uploader || uploadForumImageWithProgress;
    upload(file, (pct) => {
      setPending((prev) => prev.map((p) => (p.id === id ? { ...p, progress: pct } : p)));
    })
      .then((url) => {
        const alt = file.name.replace(/\.[^.]+$/, '');
        replacePlaceholder(placeholder, `![${alt}](${url})`);
        setPending((prev) => prev.filter((p) => p.id !== id));
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        toast({ title: 'Image uploaded' });
      })
      .catch((err) => {
        const message = err instanceof UploadError
          ? err.message
          : err instanceof Error ? err.message : 'Upload failed.';
        replacePlaceholder(placeholder, '');
        setPending((prev) => prev.map((p) => (p.id === id ? { ...p, error: message, progress: 0 } : p)));
        toast({ title: 'Upload failed', description: message, variant: 'destructive' });
      });
  };

  const handleFiles = (files: FileList | File[]) => {
    const list = Array.from(files);
    for (const f of list) {
      if (!f.type.startsWith('image/')) {
        toast({ title: 'Skipped file', description: `${f.name} is not an image.`, variant: 'destructive' });
        continue;
      }
      beginUpload(f);
    }
  };

  const retry = (id: string) => {
    const p = pending.find((x) => x.id === id);
    if (!p) return;
    setPending((prev) => prev.filter((x) => x.id !== id));
    if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
    beginUpload(p.file);
  };

  const dismiss = (id: string) => {
    setPending((prev) => {
      const p = prev.find((x) => x.id === id);
      if (p?.previewUrl) URL.revokeObjectURL(p.previewUrl);
      return prev.filter((x) => x.id !== id);
    });
  };

  const handleDrop = (e: DragEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files?.length) handleFiles(files);
  };

  const uploading = pending.some((p) => !p.error);

  return (
    <div className="rounded-md border bg-card">
      <Tabs defaultValue="write">
        <div className="flex items-center justify-between gap-2 px-2 py-1.5 border-b">
          <TabsList className="h-8">
            <TabsTrigger value="write" className="h-7 text-xs">Write</TabsTrigger>
            <TabsTrigger value="preview" className="h-7 text-xs">Preview</TabsTrigger>
          </TabsList>
          <div className="flex items-center gap-0.5">
            <ToolbarBtn label="Bold" onClick={() => wrapAtCursor('**', '**', 'bold')}><Bold className="h-3.5 w-3.5" /></ToolbarBtn>
            <ToolbarBtn label="Italic" onClick={() => wrapAtCursor('*', '*', 'italic')}><Italic className="h-3.5 w-3.5" /></ToolbarBtn>
            <ToolbarBtn label="Link" onClick={() => wrapAtCursor('[', '](https://)', 'text')}><Link2 className="h-3.5 w-3.5" /></ToolbarBtn>
            <ToolbarBtn label="List" onClick={() => insertAtCursor('\n- item')}><List className="h-3.5 w-3.5" /></ToolbarBtn>
            <ToolbarBtn label="Code" onClick={() => wrapAtCursor('`', '`', 'code')}><Code className="h-3.5 w-3.5" /></ToolbarBtn>
            <ToolbarBtn label="Image" onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />}
            </ToolbarBtn>
          </div>
        </div>

        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) handleFiles(e.target.files);
            e.target.value = '';
          }}
        />

        <TabsContent value="write" className="m-0">
          <Textarea
            ref={ref}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            rows={rows}
            maxLength={maxLength}
            onDrop={handleDrop}
            onDragEnter={() => setIsDragging(true)}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            className={`border-0 rounded-none focus-visible:ring-0 resize-y transition ${isDragging ? 'bg-primary/5' : ''}`}
            style={minHeight ? { minHeight } : undefined}
          />
        </TabsContent>

        <TabsContent value="preview" className="m-0 px-3 py-3 min-h-[140px]">
          {value.trim() ? (
            <Markdown>{value}</Markdown>
          ) : (
            <p className="text-xs text-muted-foreground italic">Nothing to preview yet.</p>
          )}
        </TabsContent>
      </Tabs>

      {pending.length > 0 && (
        <div className="border-t px-3 py-2 space-y-2">
          {pending.map((p) => (
            <div key={p.id} className="flex items-center gap-3 text-xs">
              {p.previewUrl ? (
                <img src={p.previewUrl} alt="" className="h-10 w-10 rounded object-cover border shrink-0" />
              ) : (
                <div className="h-10 w-10 rounded border bg-muted shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="truncate font-medium">{p.name}</span>
                  <span className={p.error ? 'text-destructive' : 'text-muted-foreground'}>
                    {p.error ? p.error : `${p.progress}%`}
                  </span>
                </div>
                <Progress value={p.error ? 100 : p.progress} className={p.error ? '[&>div]:bg-destructive' : ''} />
              </div>
              {p.error ? (
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7" title="Retry" onClick={() => retry(p.id)}>
                  <RotateCw className="h-3.5 w-3.5" />
                </Button>
              ) : null}
              <Button type="button" variant="ghost" size="icon" className="h-7 w-7" title="Dismiss" onClick={() => dismiss(p.id)}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between px-3 py-1.5 border-t text-[11px] text-muted-foreground">
        <span>Markdown supported · drag images here to upload</span>
        <span>{value.length}/{maxLength}</span>
      </div>
    </div>
  );
}

function ToolbarBtn({ children, onClick, label, disabled }: { children: React.ReactNode; onClick: () => void; label: string; disabled?: boolean }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-7 w-7"
      title={label}
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </Button>
  );
}
